<template>
  <div
    class="region-selector-dropdown"
    :class="dropdownOpen ? 'region-focused' : 'region-blurred'"
  >
    <div
      class="dropdown-head"
      data-testid="regionSelectorToggle"
      @click="toggleRegionDropdown"
    >
      <span class="country-name">{{ localeName }}</span>
      <span
        ><span class="paren">(</span>{{ countryCode
        }}<span class="paren">)</span></span
      >
      <span class="language language-text">{{ language }}</span>
      <NsIcon
        icon-name="icon-narrow-angle-bottom-24"
        class="region-arrow-down"
        size="custom"
      />
    </div>
    <div
      class="dropdown-body"
      data-testid="regionSelectorBody"
      @click="close"
      @mouseleave="close"
    >
      <a
        v-for="lang in otherLanguages"
        :key="lang.languageCode"
        class="dropdown-selection"
        :class="{ active: lang.languageCode === languageCode }"
        :href="lang.href"
        @click.prevent="selectLanguage(lang.languageCode, lang.href)"
      >
        <span class="language-text">{{ lang.language }}</span>
      </a>
      <a
        class="dropdown-selection"
        href="javascript:void(0)"
        @click.prevent="selectOtherRegion"
      >
        <span class="region"> {{ translations.selectOther }}... </span>
      </a>
    </div>
  </div>
</template>

<script>
import {
  events,
  RunConfigService,
  // util,
  SitesSessionStorageService,
  SitesLocalStorageService
} from "@nuskin/ns-util";
import { NsIcon } from "@nuskin/design-components";

import { isString } from "@nuskin/ns-common-lib";

const AUTO_REDIRECT_KEY = "autoRedirectGlobalLanding";
const CURRENT_PAGE_KEY = "currentPage";
const REGIONS_ALLOWED_KEY = "regionsAllowed";
const HREF_OVERRIDE_KEY = "hrefOverride";

const defaultTranslations = {
  selectOther: "Select Other"
};

export default {
  name: "NsHeaderRegionSelector",
  components: { NsIcon },
  props: {
    /**
     * If set, this will override the page redirect on the region selector page
     */
    hrefOverride: {
      type: String,
      default: ""
    },
    /**
     * Sets other available regions in the region selector dropdown
     */
    region: {
      type: Object,
      default: function() {
        return {};
      }
    },
    /**
     * Allows you to set where the "select other region" redirects too
     */
    regionSelectorPath: {
      type: String,
      default: "/content/nuskin.html#region-selection"
    },
    /**
     * If set, this will only show regions according to what is in the array
     * [ "en_US", "es_US" ]
     */
    regionsAllowed: {
      type: Array,
      default: function() {
        return [];
      }
    },
    /**
     * Translations
     */
    translations: {
      type: Object,
      default: () => defaultTranslations
    }
  },
  data() {
    return {
      languageCode: "",
      language: "",
      localeName: "",
      countryCode: "",
      otherLanguages: [],
      dropdownOpen: false
    };
  },
  watch: {
    region: {
      immediate: true,
      handler(value) {
        this.setRegionData(value);
      }
    }
  },
  methods: {
    /**
     * Returns the browser's url with the language replaced by the new one.
     */
    getHrefForLanguage(languageCode, countryCode) {
      const runConfig = RunConfigService.getRunConfig();
      const url = window.location.href;
      const locale = `${runConfig.language}_${runConfig.country}`;
      return url.replace(locale, `${languageCode}_${countryCode}`);
    },
    /**
     * Convert the provided region prop into usable data for the template
     */
    setRegionData(region) {
      const {
        languageCodes, // ["en", "vi", "es", "zh"] - (ordered)
        languages, // ["English", "Tieng Viet", "Espanol", ...] - (ordered)
        locale_names, // [{ en: "United States" }, { es: "Estados Unidos" }, ...]
        countryCode // "US"
        // paths // [{ vi: "/content/nuskin/vi_US" }, { es: "/content/nuskin/es_US" }, ...]
      } = region;

      if (languageCodes && languages && locale_names && countryCode) {
        const regions = languageCodes.map((languageCode, index) => ({
          languageCode,
          language: languages[index]
          // path:
          //   (paths.find(item => item[languageCode]) || {})[languageCode] || ""
        }));
        const currentLanguageCode = RunConfigService.getRunConfig().language;
        const currentRegion =
          regions.find(region => region.languageCode === currentLanguageCode) ||
          {};
        const currentLocale =
          locale_names.find(locale => locale[currentLanguageCode]) || {};

        const regionsAllowed =
          this.regionsAllowed.length > 0
            ? regions.filter(region =>
                this.regionsAllowed.includes(
                  `${region.languageCode}_${countryCode}`
                )
              )
            : regions;

        // set region data
        this.languageCode = currentRegion.languageCode || "";
        this.language = currentRegion.language || "";
        this.localeName = currentLocale[currentLanguageCode] || "";
        this.countryCode = countryCode;
        this.otherLanguages = regionsAllowed.map(region => ({
          languageCode: region.languageCode,
          language: region.language,
          href: this.getHrefForLanguage(region.languageCode, countryCode)
        }));
      } else {
        // clear region data
        this.languageCode = "";
        this.language = "";
        this.localeName = "";
        this.countryCode = "";
        this.otherLanguages = [];
      }
    },
    /**
     * Close the dropdown
     */
    close() {
      this.dropdownOpen = false;
    },
    /**
     * Save global variables to session storage before navigating away
     */
    selectOtherRegion() {
      if (this.regionsAllowed && this.regionsAllowed.length) {
        SitesSessionStorageService.writeItem(
          REGIONS_ALLOWED_KEY,
          JSON.stringify(this.regionsAllowed)
        );
      }

      if (this.hrefOverride) {
        SitesSessionStorageService.writeItem(
          HREF_OVERRIDE_KEY,
          isString(this.hrefOverride)
            ? this.hrefOverride
            : JSON.stringify(this.hrefOverride)
        );
      }

      SitesLocalStorageService.writeItem(
        CURRENT_PAGE_KEY,
        window.location.href
      );

      SitesLocalStorageService.removeItem(AUTO_REDIRECT_KEY);

      window.location = this.regionSelectorPath;
    },
    selectLanguage(code, href) {
      const runConfig = RunConfigService.getRunConfig();
      events.publish(
        events.header.LANGUAGE_CHANGED || "headerLanguageChanged",
        {
          oldLanguage: runConfig.language,
          country: runConfig.country,
          language: code
        }
      );
      //Only redirect if the href is different than the current page and we aren't the catalog app
      if (
        window.location.href !== href &&
        href.indexOf("/static/catalog/") === -1
      ) {
        window.location = href;
      }
    },
    toggleRegionDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@nuskin/ns-core-styles/src/scss/global.mixins";
@import "~@nuskin/ns-core-styles/src/scss/globalVariables";

.region-selector-dropdown {
  position: relative;
  margin: 0 15px;
  margin: 0 10px !important;
  padding: 0 !important;
  outline: none;
  cursor: pointer;
}

.dropdown-head {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  color: var(--color-interactive-8);
}

.rtl .dropdown-body {
  right: auto;
  left: -1px;
}

.dropdown-body {
  position: absolute;
  top: calc(100% + 2px);
  right: -1px;
  width: 200px;
  border: var(--global-border-active);
  z-index: var(--z-dropdown);
}

.dropdown-selection {
  border-top: 1px solid var(--color-gray-4);
  position: relative;
  padding: 8px 15px;
  background-color: white;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-decoration: none !important;
  @include p-small();
  transition: var(--global-transition) !important;
}

.dropdown-selection:hover {
  background-color: var(--color-interactive-5);
}

.dropdown-selection::before {
  content: "";
  width: 2px;
  height: calc(100% - 8px);
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  background-color: var(--color-interactive-7);
}

.dropdown-selection:not(.active)::before {
  @include hide();
}

.dropdown-selection.active::before {
  @include show();
}

.dropdown-selection.active {
  font-weight: 600;
  color: var(--color-interactive-7);
}

.dropdown-body > a:first-of-type {
  border-top: none;
}

.dropdown-body > a {
  color: var(--color-primary-text);
}

.dropdown-head .region-arrow-down {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-left: 10px;
  color: var(--color-interactive-7);
  transition: var(--global-transition);
}

.region-focused .dropdown-head .region-arrow-down {
  transform: scaleY(-1);
}

.dropdown-head span:empty {
  display: none;
}

.dropdown-head span {
  @include p-small();
}

.country-name {
  margin-right: 5px;
  white-space: nowrap;
}

.language {
  position: relative;
  padding-left: 10px;
}

.language::before {
  content: "";
  height: 10px;
  width: 1px;
  position: absolute;
  left: 4px;
  top: 0;
  bottom: 0;
  margin: auto;
  background-color: var(--color-interactive-8);
}

.language-text {
  text-transform: capitalize;
}

.region-blurred.region-selector-dropdown .dropdown-body {
  opacity: 0;
  visibility: hidden;
}
.region-focused.region-selector-dropdown .dropdown-body {
  opacity: 1;
  visibility: visible;
}
@media (max-width: 1199px) {
  .country-name,
  .paren {
    display: none;
  }
}

@media (min-width: 1440px) {
  .region-selector-dropdown {
    margin: 0 20px !important;
  }
  .dropdown-head .region-arrow-down {
    margin-left: 15px;
  }
}
</style>
